import { useState, useCallback } from 'react';
import {
  StartReviewResponse,
  SendReviewMessageResponse,
  GenerateReviewResponse,
  ReviewMessage,
  MAX_REVIEW_COUNT,
  MIN_REVIEW_MESSAGE_LENGTH,
  MAX_REVIEW_TURNS,
} from '@recruitai/shared';
import { apiClient } from '../lib/api-client';
import { toast } from 'sonner';

interface UseReviewChatOptions {
  jobId: string;
  candidateId: string;
  interviewId: string;
  initialReviewCount?: number;
}

interface UseReviewChatReturn {
  sessionId: string | null;
  messages: ReviewMessage[];
  isLoading: boolean;
  turnCount: number;
  canGenerate: boolean;
  reviewNumber: number;
  remainingReviews: number;
  startReview: () => Promise<void>;
  sendMessage: (text: string) => Promise<void>;
  generateReview: () => Promise<void>;
}

export function useReviewChat({
  jobId,
  candidateId,
  interviewId,
  initialReviewCount = 0,
}: UseReviewChatOptions): UseReviewChatReturn {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ReviewMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [turnCount, setTurnCount] = useState(0);
  const [canGenerate, setCanGenerate] = useState(false);
  const [reviewNumber, setReviewNumber] = useState(0);
  const [reviewCount, setReviewCount] = useState(initialReviewCount);

  const basePath = `/jobs/${jobId}/candidates/${candidateId}/interviews/${interviewId}`;

  const startReview = useCallback(async () => {
    if (isLoading) return;
    try {
      setIsLoading(true);
      const response = await apiClient.post<StartReviewResponse>(`${basePath}/review`, {});
      setSessionId(response.sessionId);
      setReviewNumber(response.reviewNumber);
      setMessages([
        { role: 'assistant', content: response.aiMessage, timestamp: new Date().toISOString() },
      ]);
      setTurnCount(0);
      setCanGenerate(false);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao iniciar revisão');
    } finally {
      setIsLoading(false);
    }
  }, [basePath, isLoading]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!sessionId || isLoading) return;
      if (text.trim().length < MIN_REVIEW_MESSAGE_LENGTH) {
        toast.error(`Mensagem deve ter no mínimo ${MIN_REVIEW_MESSAGE_LENGTH} caracteres`);
        return;
      }
      if (turnCount >= MAX_REVIEW_TURNS) {
        toast.error('Limite de turnos atingido. Gere a reavaliação.');
        return;
      }

      const userMsg: ReviewMessage = { role: 'user', content: text.trim(), timestamp: new Date().toISOString() };
      setMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);

      try {
        const response = await apiClient.post<SendReviewMessageResponse>(
          `${basePath}/review/${sessionId}/messages`,
          { message: text.trim() }
        );
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: response.aiResponse, timestamp: new Date().toISOString() },
        ]);
        setTurnCount(response.turnCount);
        setCanGenerate(response.canGenerate);
      } catch (err: any) {
        toast.error(err.message || 'Erro ao enviar mensagem');
        // Remove optimistic user message on error
        setMessages((prev) => prev.filter((m) => m !== userMsg));
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId, basePath, isLoading, turnCount]
  );

  const generateReview = useCallback(async () => {
    if (!sessionId || isLoading) return;
    try {
      setIsLoading(true);
      await apiClient.post<GenerateReviewResponse>(`${basePath}/review/${sessionId}/generate`, {});
      setReviewCount((prev) => prev + 1);
      toast.success('Reavaliação iniciada. Aguarde o processamento...');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao gerar reavaliação');
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, basePath, isLoading]);

  return {
    sessionId,
    messages,
    isLoading,
    turnCount,
    canGenerate,
    reviewNumber,
    remainingReviews: Math.max(0, MAX_REVIEW_COUNT - reviewCount),
    startReview,
    sendMessage,
    generateReview,
  };
}
